import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import JurisdictionContact from "../../../../components/Catalogs/FirmContact/JurisdictionContacts";
import NewButton from "../../../../components/Atomos/Buttons/newButton";
import Plus2 from "../../../../components/Atomos/Icons/Plus";
import Close from "../../../../components/Atomos/Icons/Close";
import FirmContactButton from "../../../../components/Atomos/Buttons/firmContactButton";
import SaveChanges from "../../../../components/Atomos/Icons/EditMode/SaveChanges";
import { fetchPersonByFirm } from "../../../../Services/Person";
import { getOfficeByFirm } from "../../../../Services/Office";
import {
  getFirmContactByFirmId,
  saveFirmContact,
} from "../../../../Services/FirmContact";
import type { FirmContactView } from "../../../../interfaces/models/IFirmContact";
import type { IOfficeNew } from "../../../../interfaces/models/IOffice";
import { getJurisdictionLabel } from "../../../../utils/jurisdiction/structuredJurisdiction";
import {
  contactsForFirmContactDropdown,
  formatPersonsForContactDropdown,
  resolveContactSelection,
  serializeFirmContactPayload,
  toFirmContactPayload,
  validateFirmContactBlock,
  type ContactDropdownPerson,
} from "../../../../utils/firmContactUtils";
import {
  getFirmContactAllowedOfficeIds,
  getPersonPickerOptionsForOffices,
  mergeFirmContactsWithOffices,
  type PersonOfficeLike,
} from "../../Professionals/manageDataAdminOfficeRules";

type LocationTab = { id: string; name?: string; jurisdiction?: string };

type EditMembershipLocationProps = {
  /** Selected office id (membership location). */
  officeId?: string;
  /** Display label for the active location. */
  jurisdictionLabel?: string;
  /** Other locations for “apply to” actions. */
  otherLocations?: LocationTab[];
  setErrorFields: (fields: string[]) => void;
  setErrorJurisdiction: (messages: string[]) => void;
  buttonClicked: string;
  /** @deprecated use officeId */
  jurisdictionId?: string;
  /** @deprecated use jurisdictionLabel */
  jurisdictionName?: string;
  /** @deprecated use otherLocations */
  jurisdictionsIds?: LocationTab[];
};

type ContactSelection = { id: string; name: string };

type SelectedContactsState = {
  primaryContact: ContactSelection;
  secondaryContact: ContactSelection;
  marketingContact: ContactSelection;
  accountingContact: ContactSelection;
  itContact: ContactSelection;
  socialMediaContact: ContactSelection;
  hrContact: ContactSelection;
  ccContact: ContactSelection;
};

const initialActiveErrors = {
  primaryContact: false,
  secondaryContact: false,
  marketingContact: false,
  accountingContact: false,
};

const ActiveErrorsMessage = {
  primaryContact: "Primary Contact is required.",
  secondaryContact: "Secondary Contact is required.",
  marketingContact: "Marketing Contact is required.",
  accountingContact: "Accounting Contact is required.",
};

const EMPTY_CONTACT: FirmContactView["primaryContact"] = {
  id: "",
  fullName: "",
  email: "",
  phone: { code: "", phone: "", basecode: "" },
};

const emptySelectedContacts = (): SelectedContactsState => ({
  primaryContact: { id: "", name: "" },
  secondaryContact: { id: "", name: "" },
  marketingContact: { id: "", name: "" },
  accountingContact: { id: "", name: "" },
  itContact: { id: "", name: "" },
  socialMediaContact: { id: "", name: "" },
  hrContact: { id: "", name: "" },
  ccContact: { id: "", name: "" },
});

function selectionFromContact(
  contact?: FirmContactView["primaryContact"],
): ContactSelection {
  return {
    id: String(contact?.id ?? ""),
    name: String(contact?.fullName ?? "").trim(),
  };
}

function contactFromSelection(
  selection: ContactSelection,
  persons: PersonOfficeLike[],
): FirmContactView["primaryContact"] {
  if (!selection.id) {
    return { ...EMPTY_CONTACT };
  }
  const person = persons.find((row) => String(row.id ?? "") === selection.id);
  return {
    id: selection.id,
    fullName: selection.name,
    email: String(person?.firmEmail ?? ""),
    phone: { ...EMPTY_CONTACT.phone },
  };
}

function buildBlockFromSelections(
  block: FirmContactView,
  selected: SelectedContactsState,
  liaisonSelections: ContactSelection[],
  persons: PersonOfficeLike[],
): FirmContactView {
  return {
    ...block,
    primaryContact: contactFromSelection(selected.primaryContact, persons),
    secondaryContact: contactFromSelection(selected.secondaryContact, persons),
    accountingContact: contactFromSelection(
      selected.accountingContact,
      persons,
    ),
    marketingContacts: selected.marketingContact.id
      ? [contactFromSelection(selected.marketingContact, persons)]
      : [],
    itContact: selected.itContact.id
      ? contactFromSelection(selected.itContact, persons)
      : undefined,
    socialMediaContact: selected.socialMediaContact.id
      ? contactFromSelection(selected.socialMediaContact, persons)
      : undefined,
    hrContact: selected.hrContact.id
      ? contactFromSelection(selected.hrContact, persons)
      : undefined,
    ccContact: selected.ccContact.id
      ? contactFromSelection(selected.ccContact, persons)
      : undefined,
    liaisonContacts: liaisonSelections
      .filter((item) => item.id)
      .map((item) => contactFromSelection(item, persons)),
  };
}

function blockToUiState(block: FirmContactView): {
  selected: SelectedContactsState;
  liaison: ContactSelection[];
} {
  return {
    selected: {
      primaryContact: selectionFromContact(block.primaryContact),
      secondaryContact: selectionFromContact(block.secondaryContact),
      marketingContact: selectionFromContact(block.marketingContacts?.[0]),
      accountingContact: selectionFromContact(block.accountingContact),
      itContact: selectionFromContact(block.itContact),
      socialMediaContact: selectionFromContact(block.socialMediaContact),
      hrContact: selectionFromContact(block.hrContact),
      ccContact: selectionFromContact(block.ccContact),
    },
    liaison: (block.liaisonContacts ?? []).map((contact) =>
      selectionFromContact(contact),
    ),
  };
}

function normalizeLocationTabs(tabs: LocationTab[] | undefined): LocationTab[] {
  return (tabs ?? [])
    .map((tab) => ({
      id: String(tab.id ?? "").trim(),
      name: String(tab.name ?? tab.jurisdiction ?? "").trim(),
    }))
    .filter((tab) => tab.id);
}

const EditMembershipLocation: React.FC<EditMembershipLocationProps> = ({
  officeId,
  jurisdictionLabel,
  otherLocations,
  setErrorFields,
  setErrorJurisdiction,
  buttonClicked,
  jurisdictionId,
  jurisdictionName,
  jurisdictionsIds,
}) => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const saveButtonRef = useRef<HTMLDivElement>(null);

  const selectedOfficeKey = String(officeId ?? jurisdictionId ?? "").trim();
  const activeLocationLabel =
    String(jurisdictionLabel ?? jurisdictionName ?? "").trim() ||
    selectedOfficeKey;

  const [checked, setChecked] = useState(false);
  const [persons, setPersons] = useState<PersonOfficeLike[]>([]);
  const [offices, setOffices] = useState<IOfficeNew[]>([]);
  const [contactBlocks, setContactBlocks] = useState<FirmContactView[]>([]);
  const [originalPayloads, setOriginalPayloads] = useState<Map<string, string>>(
    new Map(),
  );
  const [originalPersistedIds, setOriginalPersistedIds] = useState<
    Map<string, string>
  >(new Map());
  const [selectedContacts, setSelectedContacts] =
    useState<SelectedContactsState>(emptySelectedContacts);
  const [selectedLiaisonContacts, setSelectedLiaisonContacts] = useState<
    ContactSelection[]
  >([]);
  const [activeErrors, setActiveErrors] = useState(initialActiveErrors);
  const [activeErrorsMessage] = useState(ActiveErrorsMessage);

  const firmId = String(state?.id ?? "").trim();
  const firmName = String(state?.firmName ?? state?.name ?? "").trim();
  const membershipType = state?.membershipType as string | undefined;

  const resolvedOfficeId = useMemo(() => {
    if (!contactBlocks.length) {
      return selectedOfficeKey;
    }

    if (
      contactBlocks.some(
        (block) => String(block.officeId ?? "").trim() === selectedOfficeKey,
      )
    ) {
      return selectedOfficeKey;
    }

    if (activeLocationLabel) {
      const byLabel = contactBlocks.find(
        (block) =>
          String(block.jurisdiction ?? "").trim() === activeLocationLabel,
      );
      if (byLabel?.officeId) {
        return String(byLabel.officeId).trim();
      }
    }

    const legacyJurisdictions = state?.firmContact?.jurisdictions;
    if (Array.isArray(legacyJurisdictions)) {
      const legacyIndex = legacyJurisdictions.findIndex(
        (item: { jurisdiction?: string }) =>
          String(item.jurisdiction ?? "") === selectedOfficeKey,
      );
      if (legacyIndex >= 0 && contactBlocks[legacyIndex]?.officeId) {
        return String(contactBlocks[legacyIndex].officeId).trim();
      }
    }

    return String(contactBlocks[0]?.officeId ?? selectedOfficeKey).trim();
  }, [
    contactBlocks,
    selectedOfficeKey,
    activeLocationLabel,
    state?.firmContact?.jurisdictions,
  ]);

  const locationTabs = useMemo(() => {
    const explicit = normalizeLocationTabs(otherLocations ?? jurisdictionsIds);
    if (explicit.length) {
      return explicit.filter((tab) => tab.id !== resolvedOfficeId);
    }
    return contactBlocks
      .filter(
        (block) => String(block.officeId ?? "").trim() !== resolvedOfficeId,
      )
      .map((block) => ({
        id: String(block.officeId ?? "").trim(),
        name: String(block.jurisdiction ?? "").trim() || String(block.officeId),
      }))
      .filter((tab) => tab.id);
  }, [otherLocations, jurisdictionsIds, contactBlocks, resolvedOfficeId]);

  const dropdownPersons = useMemo((): ContactDropdownPerson[] => {
    if (!resolvedOfficeId) {
      return formatPersonsForContactDropdown(persons);
    }
    const allowedOfficeIds = getFirmContactAllowedOfficeIds(
      offices,
      resolvedOfficeId,
    );
    return getPersonPickerOptionsForOffices(allowedOfficeIds, persons).map(
      (option) => {
        const person = persons.find(
          (row) => String(row.id ?? "") === String(option.id),
        );
        return {
          id: option.id,
          name: option.name,
          isAttorney: person?.isAttorney,
        };
      },
    );
  }, [offices, resolvedOfficeId, persons]);

  useEffect(() => {
    if (!firmId) {
      return;
    }

    const fromNavigation = formatPersonsForContactDropdown(state?.personList);
    if (fromNavigation.length > 0) {
      setPersons(
        fromNavigation.map((person) => ({
          id: person.id,
          name: person.name,
          lastName: "",
          isAttorney: person.isAttorney,
        })),
      );
    }

    let cancelled = false;

    const load = async () => {
      try {
        const [officesData, firmContactData, personRows] = await Promise.all([
          getOfficeByFirm(firmId),
          getFirmContactByFirmId(firmId),
          fromNavigation.length
            ? Promise.resolve([])
            : fetchPersonByFirm(firmId),
        ]);
        if (cancelled) {
          return;
        }

        const officeList = Array.isArray(officesData) ? officesData : [];
        const firmContacts = Array.isArray(firmContactData)
          ? firmContactData
          : firmContactData
            ? [firmContactData]
            : [];

        const merged = mergeFirmContactsWithOffices(
          firmContacts as FirmContactView[],
          officeList,
          firmId,
          firmName,
          membershipType,
        );

        const persistedIds = new Map<string, string>();
        for (const contact of firmContacts as FirmContactView[]) {
          const officeKey = String(contact.officeId ?? "").trim();
          const persistedId = String(contact.id ?? "").trim();
          if (officeKey && persistedId) {
            persistedIds.set(officeKey, persistedId);
          }
        }

        const payloads = new Map<string, string>();
        for (const contact of merged) {
          const officeKey = String(contact.officeId ?? "").trim();
          if (!officeKey) {
            continue;
          }
          payloads.set(
            officeKey,
            serializeFirmContactPayload(
              toFirmContactPayload(contact, persistedIds.get(officeKey)),
            ),
          );
        }

        setOffices(officeList);
        setContactBlocks(merged);
        setOriginalPersistedIds(persistedIds);
        setOriginalPayloads(payloads);

        if (!fromNavigation.length && Array.isArray(personRows)) {
          setPersons(personRows);
        }
      } catch (error) {
        console.error("Error loading firm contact data:", error);
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [firmId, firmName, membershipType, state?.personList]);

  useEffect(() => {
    if (!resolvedOfficeId || !contactBlocks.length) {
      return;
    }
    const block = contactBlocks.find(
      (item) => String(item.officeId ?? "").trim() === resolvedOfficeId,
    );
    if (!block) {
      return;
    }

    const ui = blockToUiState(block);
    setSelectedContacts(ui.selected);
    setSelectedLiaisonContacts(ui.liaison);
  }, [resolvedOfficeId, contactBlocks]);

  const handleDropDownChange = (key: string, contact: ContactSelection) => {
    setSelectedContacts((prev) => {
      const nextSelected = {
        ...prev,
        [key]: { id: contact.id, name: contact.name ?? "" },
      };

      setContactBlocks((blocks) =>
        blocks.map((block) => {
          if (String(block.officeId ?? "").trim() !== resolvedOfficeId) {
            return block;
          }
          const ui = blockToUiState(block);
          return buildBlockFromSelections(
            block,
            nextSelected,
            ui.liaison,
            persons,
          );
        }),
      );

      return nextSelected;
    });
  };

  const handleDropDownChangeLiaison = (
    index: number,
    contact: ContactSelection,
  ) => {
    setSelectedLiaisonContacts((prevLiaison) => {
      const nextLiaison = [...prevLiaison];
      nextLiaison[index] = { id: contact.id, name: contact.name ?? "" };

      setContactBlocks((blocks) =>
        blocks.map((block) => {
          if (String(block.officeId ?? "").trim() !== resolvedOfficeId) {
            return block;
          }
          const ui = blockToUiState(block);
          return buildBlockFromSelections(
            block,
            ui.selected,
            nextLiaison,
            persons,
          );
        }),
      );

      return nextLiaison;
    });
  };

  const handleAddNewContact = () => {
    setSelectedLiaisonContacts((prev) => [...prev, { id: "", name: "" }]);
  };

  const applyContactToOffices = (
    officeIds: LocationTab[],
    contactObject: ContactSelection,
    type: string,
  ) => {
    if (!contactObject.id) {
      return;
    }
    setContactBlocks((prev) =>
      prev.map((block) => {
        const officeKey = String(block.officeId ?? "").trim();
        if (!officeIds.some((tab) => tab.id === officeKey)) {
          return block;
        }

        const selected = {
          ...emptySelectedContacts(),
          ...blockToUiState(block).selected,
          [type]: contactObject,
        };
        return buildBlockFromSelections(
          block,
          selected,
          blockToUiState(block).liaison,
          persons,
        );
      }),
    );
  };

  const handleApplyContact = (
    officeIds: LocationTab[],
    contactObject: ContactSelection,
    type: string,
  ) => {
    applyContactToOffices(officeIds, contactObject, type);
  };

  const handleApplyAllContact = (
    contactObject: ContactSelection,
    type: string,
  ) => {
    applyContactToOffices(locationTabs, contactObject, type);
  };

  const handleApplyContactLiaison = (
    officeIds: LocationTab[],
    contactObject: ContactSelection,
  ) => {
    if (!contactObject.id) {
      return;
    }
    setContactBlocks((prev) =>
      prev.map((block) => {
        const officeKey = String(block.officeId ?? "").trim();
        if (!officeIds.some((tab) => tab.id === officeKey)) {
          return block;
        }

        const ui = blockToUiState(block);
        if (ui.liaison.some((item) => item.id === contactObject.id)) {
          return block;
        }
        return buildBlockFromSelections(
          block,
          ui.selected,
          [...ui.liaison, contactObject],
          persons,
        );
      }),
    );
  };

  const handleApplyAllContactLiaison = (contactObject: ContactSelection) => {
    handleApplyContactLiaison(locationTabs, contactObject);
  };

  useEffect(() => {
    const newErrorFields: string[] = [];
    if (activeErrors.primaryContact) {
      newErrorFields.push("Primary Contact");
    }
    if (activeErrors.secondaryContact) {
      newErrorFields.push("Secondary Contact");
    }
    if (activeErrors.marketingContact) {
      newErrorFields.push("Marketing Contact");
    }
    if (activeErrors.accountingContact) {
      newErrorFields.push("Accounting Contact");
    }
    setErrorFields(newErrorFields);
  }, [activeErrors, setErrorFields]);

  const checkOtherLocationContacts = useCallback(() => {
    const blocksWithErrors = contactBlocks.filter((block) => {
      const officeKey = String(block.officeId ?? "").trim();
      if (!officeKey || officeKey === resolvedOfficeId) {
        return false;
      }
      return validateFirmContactBlock(block) !== null;
    });

    if (blocksWithErrors.length > 0) {
      setErrorJurisdiction([
        "Check Other Membership Location Contacts:",
        ...blocksWithErrors.map(
          (block) =>
            getJurisdictionLabel({ jurisdiction: block.jurisdiction }) ||
            block.officeId ||
            "Unknown location",
        ),
      ]);
    }

    return blocksWithErrors;
  }, [resolvedOfficeId, contactBlocks, setErrorJurisdiction]);

  useEffect(() => {
    setErrorJurisdiction([]);
    if (checked) {
      checkOtherLocationContacts();
    }
  }, [
    resolvedOfficeId,
    checked,
    checkOtherLocationContacts,
    setErrorJurisdiction,
  ]);

  const validateActiveBlock = () => {
    const activeBlock = contactBlocks.find(
      (block) => String(block.officeId ?? "").trim() === resolvedOfficeId,
    );
    if (!activeBlock) {
      return true;
    }

    const primaryInput = !selectedContacts.primaryContact.id;
    const secondaryInput = !selectedContacts.secondaryContact.id;
    const accountingInput = !selectedContacts.accountingContact.id;
    const marketingInput = !selectedContacts.marketingContact.id;

    const newActiveErrors = {
      primaryContact: primaryInput,
      secondaryContact: secondaryInput,
      marketingContact: marketingInput,
      accountingContact: accountingInput,
    };
    setActiveErrors(newActiveErrors);

    return (
      newActiveErrors.primaryContact ||
      newActiveErrors.secondaryContact ||
      newActiveErrors.marketingContact ||
      newActiveErrors.accountingContact
    );
  };

  const handleUpdate = async () => {
    try {
      setChecked(true);
      const blocksWithErrors = checkOtherLocationContacts();

      if (buttonClicked !== "saveOverride") {
        if (validateActiveBlock()) {
          return;
        }
        if (blocksWithErrors.length > 0) {
          return;
        }
      }

      const activeBlock = contactBlocks.find(
        (block) => String(block.officeId ?? "").trim() === resolvedOfficeId,
      );
      const blocksToSave = activeBlock
        ? contactBlocks.map((block) =>
            String(block.officeId ?? "").trim() === resolvedOfficeId
              ? buildBlockFromSelections(
                  block,
                  selectedContacts,
                  selectedLiaisonContacts,
                  persons,
                )
              : block,
          )
        : contactBlocks;

      const payloads = blocksToSave.map((block) =>
        toFirmContactPayload(
          block,
          originalPersistedIds.get(String(block.officeId ?? "").trim()),
        ),
      );

      const changedPayloads = payloads.filter((payload) => {
        const key = String(payload.officeId ?? "").trim();
        const previous = originalPayloads.get(key);
        return serializeFirmContactPayload(payload) !== previous;
      });

      for (const payload of changedPayloads) {
        await saveFirmContact(payload);
      }

      if (changedPayloads.length === 0) {
        return;
      }

      setTimeout(() => {
        navigate(`/ManageFirm/GeneralTab/${firmId}`, {
          state: { ...state, selectedTab: "GeneralTab" },
        });
      }, 500);
    } catch (error) {
      console.error("Error updating firm contacts:", error);
    }
  };

  useEffect(() => {
    if (checked) {
      validateActiveBlock();
    }
  }, [selectedContacts, checked]);

  const dropdowns = [
    {
      key: "primaryContact",
      title: "Primary Contact",
      selectedObjst: selectedContacts.primaryContact,
      required: true,
      error: activeErrors.primaryContact,
      errorMsj: activeErrorsMessage.primaryContact,
    },
    {
      key: "secondaryContact",
      title: "Secondary Contact",
      selectedObjst: selectedContacts.secondaryContact,
      required: true,
      error: activeErrors.secondaryContact,
      errorMsj: activeErrorsMessage.secondaryContact,
    },
    {
      key: "marketingContact",
      title: "Marketing Contact",
      selectedObjst: selectedContacts.marketingContact,
      required: true,
      error: activeErrors.marketingContact,
      errorMsj: activeErrorsMessage.marketingContact,
    },
    {
      key: "accountingContact",
      title: "Accounting Contact",
      selectedObjst: selectedContacts.accountingContact,
      required: true,
      error: activeErrors.accountingContact,
      errorMsj: activeErrorsMessage.accountingContact,
    },
    {
      key: "itContact",
      title: "IT Contact",
      selectedObjst: selectedContacts.itContact,
    },
    {
      key: "socialMediaContact",
      title: "Social Media Contact",
      selectedObjst: selectedContacts.socialMediaContact,
    },
    {
      key: "hrContact",
      title: "HR Contact",
      selectedObjst: selectedContacts.hrContact,
    },
    {
      key: "ccContact",
      title: "CC - Contact",
      selectedObjst: selectedContacts.ccContact,
    },
  ];

  return (
    <div className="flex flex-col gap-10 lg:pt-0 pt-6">
      {activeLocationLabel ? (
        <p className="text-gray-font text-lg font-medium">
          {activeLocationLabel}
        </p>
      ) : null}

      <div className="flex flex-col gap-12 lg:gap-5">
        {dropdowns.map((dropdown, index) => (
          <div key={index} className="grid col-span-3 gap-1 w-full">
            <JurisdictionContact
              title={dropdown.title}
              selectedObjst={dropdown.selectedObjst?.name ?? ""}
              object={dropdown.selectedObjst}
              type={dropdown.key}
              getid={true}
              seter={(id: string) => {
                const selected = resolveContactSelection(dropdownPersons, id);
                handleDropDownChange(dropdown.key, selected);
              }}
              error={dropdown.error ?? false}
              errorMsj={dropdown.errorMsj ?? ""}
              required={dropdown.required ?? false}
              contacts={contactsForFirmContactDropdown(
                dropdownPersons,
                dropdown.key,
                dropdown.selectedObjst?.id,
              )}
              onApplyContact={handleApplyContact}
              onApplyAllContact={handleApplyAllContact}
              jurisdictions={locationTabs}
              jurisdictionId={resolvedOfficeId}
            />
          </div>
        ))}

        {selectedLiaisonContacts.map((dropdown, index) => (
          <div key={index} className="grid col-span-3 gap-1 w-full">
            <JurisdictionContact
              title={`Liaison Contact ${index + 1}`}
              getid={true}
              object={dropdown}
              selectedObjst={selectedLiaisonContacts[index]?.name ?? ""}
              seter={(id: string) => {
                const selected = resolveContactSelection(dropdownPersons, id);
                handleDropDownChangeLiaison(index, selected);
              }}
              error={false}
              contacts={contactsForFirmContactDropdown(
                dropdownPersons,
                "liaisonContact",
                selectedLiaisonContacts[index]?.id,
              )}
              onApplyContact={(officeIds, contactObject) =>
                handleApplyContactLiaison(officeIds, contactObject)
              }
              onApplyAllContact={handleApplyAllContactLiaison}
              jurisdictions={locationTabs}
              jurisdictionId={resolvedOfficeId}
            />
          </div>
        ))}
      </div>

      <div className="w-full md:w-min">
        <NewButton
          text="NEW CONTACT"
          color="accent"
          type="button"
          style="filled"
          size="large"
          content="textIcon"
          onClick={handleAddNewContact}
          icon={
            <Plus2 className="w-5 h-5 shrink-0 relative overflow-visible" />
          }
        />
      </div>

      <div className="flex justify-end self-stretch w-full">
        <div className="flex w-full md:w-min gap-4">
          <NewButton
            text="CANCEL"
            color="neutral"
            content="textIcon"
            type="button"
            style="outlined"
            size="medium"
            icon={<Close />}
            onClick={() => navigate(-1)}
          />
          <div className="w-full min-w-[8.75rem]">
            <FirmContactButton
              text="SAVE"
              color="accent"
              style="filled"
              size="medium"
              content="textIcon"
              icon={<SaveChanges />}
              onClick={handleUpdate}
              ref={saveButtonRef}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditMembershipLocation;
